/*
* Order search for authenticated users
*/

import '@progress/kendo-ui';

import { Grid } from '@progress/kendo-grid-react-wrapper';
import * as _ from 'lodash';
import * as React from 'react';
import { Row } from 'react-bootstrap';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { push } from 'react-router-redux';
import VirtualizedSelect from 'react-virtualized-select';
import { compose } from 'redux';
import { connectRequest, updateEntities } from 'redux-query';

import * as Actions from '../actions';
import * as AppComp from '../common/app-component-base';
import { BootstrapColXl as Col } from '../common/bootstrap/bootstrapcolxl';
import { FormUtilities } from '../common/formutilities';
import { GridUtilities } from '../common/grid/gridutilities';
import { NavigationLinks } from '../common/navigation';
import { WhiteBoxPageSection } from '../common/whiteboxpagesection';
import ApiEndpoints from '../config/apiendpoints';
import * as ReduxQueries from '../queries';
import { initialState } from 'src/reducers/searchFiltersOrdersReducer';

export interface IOrderSearchProps extends AppComp.IAppComponentProps {
    filterByAssociations?: any;
    filterByClients?: any;
    filterByStatuses?: any;
    navigateTo?: any;
}

class OrderSearch extends AppComp.AppComponent<IOrderSearchProps, any> {
    public gridOptions = GridUtilities.kendoGridOptionsPaging(
        [
            GridUtilities.kendoGridColumnDefinition('orderId', this.appTextGridColumnHeader('order-id'), 50, 1),
            GridUtilities.kendoGridColumnDefinition('associationId', this.appTextGridColumnHeader('association-id'), 50, 1),
            GridUtilities.kendoGridColumnDefinition('associationName', this.appTextGridColumnHeader('association-name'), 180, 2),
            GridUtilities.kendoGridColumnDefinition('associationType', this.appTextGridColumnHeader('association-type'), 80, 3),
            GridUtilities.kendoGridColumnDefinition('associationCity', this.appTextGridColumnHeader('association-city'), 80, 4),
            GridUtilities.kendoGridColumnDefinition('associationState', this.appTextGridColumnHeader('association-state'), 60, 5),
            GridUtilities.kendoGridColumnDefinition('associationZip', this.appTextGridColumnHeader('association-zip'), 50, 6),
            GridUtilities.kendoGridColumnDefinition('orderStatus', this.appTextGridColumnHeader('order-status'), 100, 7),
            GridUtilities.kendoGridColumnDefinition('viewOrderLink', '', 90, 12,
                                                    GridUtilities.kendoGridLinkColumnTemplate('viewOrderLink', 'viewOrderLinkHref', this.appText('view-order-link-title')),
                                                    { headerTemplate: '' }),
            GridUtilities.kendoGridColumnDefinition('viewInventoryLink', '', 90, 12,
                                                    GridUtilities.kendoGridLinkColumnTemplate('viewInventoryLink', 'viewInventoryLinkHref', this.appText('view-inventory-link-title')),
                                                    { headerTemplate: '' }),
        ],
        this.appText('no-results'), {}, this.appTextPageTitle()
    );

    public constructor(props: any) {
        super(props, 'app.order-search.');
    }

    public componentDidMount() {
        const enforce = this.enforceAdminOrEmployeeOnly();

        if (enforce) {
            this.props.navigateTo(NavigationLinks.userDashboard());
        }
    }

    public extendGridData(records: Array<any>) {
        // Extend grid row data
        if (!records) { return []; }

        return records.map(r => {
            let record = _.cloneDeep(r);

            record.associationName = _.get(record, 'association.name', '');
            record.associationType = _.get(record, 'association.associationType.name', '');
            record.associationCity = _.get(record, 'association.location.city', '');
            record.associationState = _.get(record, 'association.location.stateCode', '');
            record.associationZip = _.get(record, 'association.location.postalCode', '');
            record.orderStatus = _.get(record, 'status.name', '');

            // View order link
            record.viewOrderLink = '';
            record.viewOrderLinkHref = '';
            if (record.orderId && record.associationId) {
                record.viewOrderLink = this.appText('view-order');
                // record.viewOrderLinkHref = NavigationLinks.orderEdit(record.orderId);
                record.viewOrderLinkHref = NavigationLinks.orderDetail(record.orderId);
            }

            // View inventory link
            record.viewInventoryLink = '';
            record.viewInventoryLinkHref = '';
            if (record.association && record.associationId) {
                record.viewInventoryLink = this.appText('view-inventory');
                record.viewInventoryLinkHref = NavigationLinks.associationInventory(record.associationId);
            }

            return record;
        });
    }

    public extendAssociations(records: Array<any>) {
        // Add dynamic fields to association records
        if (!records) { return []; }

        let options = FormUtilities.addAnyOption(records, { associationId: 0, name: '' }, this.appTextFilterOptionAny());

        return options.map(record => {
            if (!record.label) {
                record.label = record.name;
            }

            return record;
        });
    }

    public extendClients(records: Array<any>) {
        // Add dynamic fields to client records
        if (!records) { return []; }

        let options = FormUtilities.addAnyOption(records, { clientId: 0, name: '' }, this.appTextFilterOptionAny());

        return options.map(record => {
            if (!record.label) {
                record.label = record.name;
            }

            return record;
        });
    }

    public extendStatuses(records: Array<any>) {
        // Add dynamic fields to status records
        if (!records) { return []; }

        let options = FormUtilities.addAnyOption(records, { statusId: -1, name: '' }, this.appTextFilterOptionAny());

        return options.map(record => {
            if (!record.label) {
                record.label = record.name;
            }

            return record;
        });
    }

    public handleSelectAssociation(e: any) {
        const value = _.get(e, 'value', '');
        this.props.dispatch(Actions.searchOrdersFilterAssociationId(value));
    }

    public handleSelectClient(e: any) {
        const value = _.get(e, 'value', '');
        this.props.dispatch(Actions.searchOrdersFilterClientId(value));
    }

    public handleSelectStatus(e: any) {
        const value = _.get(e, 'value', '');
        this.props.dispatch(Actions.searchOrdersFilterStatusId(value));
    }

    public render() {
        const rows = this.extendGridData(this.props.search.results);
        const dataSource = GridUtilities.kendoGridDataSourcePaged(rows);

        // Prepare select dropdown options
        const associations = FormUtilities.getExtendedFilterOptions(this.props.filterByAssociations, 'associationId', 'label', this.appTextFilterOptionAny(), -1);
        const clients = FormUtilities.getExtendedFilterOptions(this.props.filterByClients, 'clientId', 'label', this.appTextFilterOptionAny(), -1);
        const statuses = FormUtilities.getExtendedFilterOptions(this.props.filterByStatuses, 'statusId', 'statusId', this.appTextFilterOptionAny(), -1);

        const pageTitle = this.appTextResultsAndPageTitle(rows.length);
        const page = (
            <WhiteBoxPageSection title={pageTitle}>
                <Row>
                    <Col xs={12}>
                        <form onSubmit={e => { e.preventDefault(); }}>
                            <Row className="form-group">
                                <Col xs={4} xl={3}>
                                    <label className="control-label">{this.appText('filter-by-association')}</label>
                                    <VirtualizedSelect
                                        clearable={false}
                                        filterOptions={associations.filter}
                                        name="association-filter"
                                        onChange={e => this.handleSelectAssociation(e)}
                                        options={associations.options}
                                        value={this.props.search.filters.associationId}
                                    />
                                </Col>

                                <Col xs={4} xl={3}>
                                    <label className="control-label">{this.appText('filter-by-client')}</label>
                                    <VirtualizedSelect
                                        clearable={false}
                                        filterOptions={clients.filter}
                                        name="client-filter"
                                        onChange={e => this.handleSelectClient(e)}
                                        options={clients.options}
                                        value={this.props.search.filters.clientId}
                                    />
                                </Col>

                                <Col xs={4} xl={3}>
                                    <label className="control-label">{this.appText('filter-by-status')}</label>
                                    <VirtualizedSelect
                                        clearable={false}
                                        filterOptions={statuses.filter}
                                        name="status-filter"
                                        onChange={e => this.handleSelectStatus(e)}
                                        options={statuses.options}
                                        value={this.props.search.filters.statusId}
                                    />
                                </Col>
                            </Row>
                        </form>
                    </Col>
                </Row>

                <Row>
                    <Col xs={12}>
                        <Grid dataSource={dataSource} {...this.gridOptions} />
                    </Col>
                </Row>
            </WhiteBoxPageSection>
        );

        const { ...rest } = this.props;
        return (
            <AppComp.RenderPageWithLayout branding={this.props.branding} pageName={pageTitle} {...rest}>
                {page}
            </AppComp.RenderPageWithLayout>
        );
    }
}

const getFiltersApiUrl = () => ApiEndpoints.getOrdersSearchFiltersUrl();
const getApiUrl = () => ApiEndpoints.getOrdersSearchUrl();

const mapPropsToConfigs = (props) => {
    const associationId = _.get(props, 'search.filters.associationId', '');
    const clientId = _.get(props, 'search.filters.clientId', '');
    const statusId = _.get(props, 'search.filters.statusId', '');
    const operator = _.get(props, 'search.filters.operator', '');

    const currentFilters = {
        associationId,
        clientId,
        statusId,
    };

    const isInitialFilters = _.isEqual(currentFilters, initialState);

    if (isInitialFilters) {
        const haveFilterOptions = (
            (props.filterByAssociations && props.filterByAssociations.length > 0) ||
            (props.filterByClients && props.filterByClients.length > 0) ||
            (props.filterByStatuses && props.filterByStatuses.length > 0)
        );

        if (haveFilterOptions) {
            const hasResults = Array.isArray(_.get(props, 'search.results')) && _.get(props, 'search.results.length', 0) > 0;

            if (props.dispatch && hasResults) {
                props.dispatch(updateEntities({ searchOrdersResults: () => [] }));
            }

            return null;
        }

        return (
            ReduxQueries.Queries.searchOrdersFiltersRequest(
                getFiltersApiUrl(),
                ReduxQueries.PropsAccessToken(props),
            )
        );
    }

    return (
        ReduxQueries.Queries.searchOrdersRequest(
            getApiUrl(),
            ReduxQueries.PropsAccessToken(props),
            associationId,
            clientId,
            statusId,
            operator,
        )
    );
};

const mapStateToProps = (state, props) => {
    return ReduxQueries.MapQueryStateToPropsByUrl(getApiUrl(), state, props, {
        filterByAssociations: state.entities.searchOrdersAssociations,
        filterByClients: state.entities.searchOrdersClients,
        filterByStatuses: state.entities.searchOrdersStatuses,
        search: {
            results: state.entities.searchOrdersResults,
            filters: {
                associationId: state.searchFiltersOrders.associationId,
                clientId: state.searchFiltersOrders.clientId,
                statusId: state.searchFiltersOrders.statusId,
                operator: 'AND'
            }
        },
    });
};

const mapDispatchToProps = dispatch => ({
    dispatch: (d) => dispatch(d),
    navigateTo: (path) => dispatch(push(path)),
});

const OrderSearchContainer = compose<any>(
    connect(mapStateToProps, mapDispatchToProps),
    connectRequest(mapPropsToConfigs)
)(OrderSearch);

export default withRouter(OrderSearchContainer);



// WEBPACK FOOTER //
// ./src/order-search/index.tsx